import React, { useEffect, useRef } from "react";

/**
 * HubSpotForm — renders the Janté HubSpot contact form on demand.
 * Loads the official `hbspt` script once (cached at the window level)
 * then calls `hbspt.forms.create` with our portal + form ids.
 *
 * Used everywhere the site previously had a custom form (host
 * application, guest contact, jewel-store booking, concierge intake,
 * marketing consent, etc.) so every inbound lead lands in HubSpot.
 */
const HS_SCRIPT_SRC = "https://js-na2.hsforms.net/forms/embed/v2.js";
const PORTAL_ID = "242086948";
const FORM_ID_DEFAULT = "8d544375-fe97-424f-a9d6-930ce2bc930f";
// Dedicated Hosting application form — routes submissions to the Janté
// Hosts pipeline / hosts inbox automatically via a HubSpot workflow.
const FORM_ID_HOST = "79519582-f265-466c-8dbe-e429b76c84f6";
const REGION = "na2";

const FORM_ID_BY_CONTEXT = {
  // Anything starting with "host-" uses the dedicated Hosts intake form.
  host: FORM_ID_HOST,
};

function resolveFormId(context = "general") {
  if (typeof context === "string") {
    const ctxLower = context.toLowerCase();
    if (ctxLower.startsWith("host")) return FORM_ID_HOST;
  }
  return FORM_ID_BY_CONTEXT[context] || FORM_ID_DEFAULT;
}

function loadScript() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.__hsFormsScriptPromise) return window.__hsFormsScriptPromise;
  window.__hsFormsScriptPromise = new Promise((resolve, reject) => {
    if (window.hbspt) return resolve();
    const existing = document.querySelector(`script[src="${HS_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = HS_SCRIPT_SRC;
    s.async = true;
    s.charset = "utf-8";
    s.type = "text/javascript";
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return window.__hsFormsScriptPromise;
}

export default function HubSpotForm({ context = "general", className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    let mounted = true;
    loadScript()
      .then(() => {
        if (!mounted || !window.hbspt || !ref.current) return;
        // Use replaceChildren to clear without innerHTML (XSS-safe even though
        // we'd be assigning an empty string — keeps static analysers happy).
        ref.current.replaceChildren();
        window.hbspt.forms.create({
          portalId: PORTAL_ID,
          formId: resolveFormId(context),
          region: REGION,
          target: `#${ref.current.id}`,
        });
      })
      .catch(() => {
        if (!mounted || !ref.current) return;
        // Build the fallback message via DOM APIs — never innerHTML.
        ref.current.replaceChildren();
        const p = document.createElement("p");
        p.style.color = "#C41E3A";
        p.style.fontSize = "14px";
        p.textContent = "The form failed to load. Please email ";
        const a = document.createElement("a");
        a.href = "mailto:Concierge@JanteTravelConcierges.com";
        a.textContent = "Concierge@JanteTravelConcierges.com";
        p.appendChild(a);
        p.appendChild(document.createTextNode("."));
        ref.current.appendChild(p);
      });
    return () => {
      mounted = false;
    };
  }, [context]);

  // Stable DOM id so HubSpot's script can find the target.
  const id = `hs-form-${context}`;
  return <div id={id} ref={ref} className={className} data-testid={`hubspot-form-${context}`} />;
}
